import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { auth, db } from "../firebaseConfig";

const TAG_HINTS = {
  "@kontrolozet": "Açık, bugün görülen, son 7 gün ve çözülen uygunsuzluk özetini tablo olarak ekler.",
  "@kontrolacikeksiklikler": "Çözülmemiş kontrol uygunsuzluklarını detaylı tablo olarak ekler.",
  "@kontrolbugunuygunsuzluklar": "Bugün görülen kontrol uygunsuzluklarını tablo olarak ekler.",
  "@kontrolson7gunuygunsuzluklar": "Son 7 günde görülen kontrol uygunsuzluklarını tablo olarak ekler.",
};

const freqMap = {
  "monthly-1": "aylik_1",
  "monthly-first-monday": "aylik_ilk_pzt",
  "weekly-first-monday": "haftalik_ilk_pzt",
  daily: "daily",
  special: "ozel_tarih",
};

const defaultBody = `<div style="font-family:Arial,sans-serif">
  <h3>Kontrol Formları Uygunsuzluk Raporu</h3>
  <p>Özet</p>
  @kontrolozet
  <h4>Açık uygunsuzluklar</h4>
  @kontrolacikeksiklikler
</div>`;

const formatDate = (value) => {
  const date = value?.toDate ? value.toDate() : value?.seconds ? new Date(value.seconds * 1000) : null;
  return date ? date.toLocaleString("tr-TR") : "-";
};

export default function MailSchedulerControl() {
  const [emailOptions, setEmailOptions] = useState([]);
  const [toInput, setToInput] = useState("");
  const [ccInput, setCcInput] = useState("");
  const [bccInput, setBccInput] = useState("");
  const [subject, setSubject] = useState("Kontrol Formları Uygunsuzluk Raporu");
  const [periodType, setPeriodType] = useState("daily");
  const [specialDate, setSpecialDate] = useState("");
  const [sendHour, setSendHour] = useState("08:00");
  const [duration, setDuration] = useState("30");
  const [htmlBody, setHtmlBody] = useState(defaultBody);
  const [attachExcel, setAttachExcel] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scheduledList, setScheduledList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMail, setSelectedMail] = useState(null);

  useEffect(() => {
    (async () => {
      const snap = await getDocs(collection(db, "users"));
      const mails = snap.docs.map((d) => d.data()?.email).filter(Boolean);
      setEmailOptions(Array.from(new Set(mails)).sort((a, b) => a.localeCompare(b, "tr")));
    })();
  }, []);

  const fetchMails = async () => {
    const snap = await getDocs(collection(db, "scheduledMails"));
    const list = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((item) => item.module === "kontrol-formlari")
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
    setScheduledList(list);
  };

  useEffect(() => {
    fetchMails();
  }, []);

  const parsedTo = useMemo(
    () => toInput.split(",").map((item) => item.trim()).filter(Boolean),
    [toInput]
  );
  const parsedCc = useMemo(
    () => ccInput.split(",").map((item) => item.trim()).filter(Boolean),
    [ccInput]
  );
  const parsedBcc = useMemo(
    () => bccInput.split(",").map((item) => item.trim()).filter(Boolean),
    [bccInput]
  );

  const highlightedPreview = useMemo(() => {
    return (htmlBody || "").replace(/@[^\s<]+/gu, (match) => {
      const title = TAG_HINTS[match.toLowerCase()] || "Etiket";
      return `<strong title="${title}" style="color:#0f766e">${match}</strong>`;
    });
  }, [htmlBody]);

  const filteredList = useMemo(() => {
    const needle = searchTerm.trim().toLocaleLowerCase("tr-TR");
    if (!needle) return scheduledList;
    return scheduledList.filter((item) =>
      [item.subject, Array.isArray(item.to) ? item.to.join(", ") : ""]
        .some((value) => String(value || "").toLocaleLowerCase("tr-TR").includes(needle))
    );
  }, [scheduledList, searchTerm]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!parsedTo.length) return alert("Alıcı girin.");
    if (!subject.trim()) return alert("Konu girin.");
    if (!htmlBody.trim()) return alert("İçerik girin.");

    const [hour, minute] = sendHour.split(":").map(Number);
    const now = new Date();
    const frequency = freqMap[periodType];

    let nextRun;
    if (frequency === "ozel_tarih") {
      if (!specialDate) return alert("Özel tarih seçin.");
      const [y, m, d] = specialDate.split("-").map(Number);
      nextRun = new Date(y, m - 1, d, hour, minute);
    } else {
      nextRun = new Date(now);
      nextRun.setHours(hour, minute, 0, 0);
      if (nextRun <= now && frequency === "daily") nextRun.setDate(nextRun.getDate() + 1);
    }

    const payload = {
      module: "kontrol-formlari",
      to: parsedTo,
      cc: parsedCc,
      bcc: parsedBcc,
      frequency,
      ozelTarih: periodType === "special" ? specialDate : null,
      time: sendHour,
      duration: Number(duration),
      contentHtml: htmlBody,
      contentRaw: null,
      attachControlExcel: attachExcel,
      subject: subject.trim(),
      createdAt: serverTimestamp(),
      createdBy: auth?.currentUser?.email || "anonymous",
      active: true,
      nextRun,
    };

    try {
      setSaving(true);
      await addDoc(collection(db, "scheduledMails"), payload);
      alert("Mail görevi kaydedildi.");
      setToInput("");
      setCcInput("");
      setBccInput("");
      setSubject("Kontrol Formları Uygunsuzluk Raporu");
      setPeriodType("daily");
      setSpecialDate("");
      setSendHour("08:00");
      setDuration("30");
      setAttachExcel(true);
      setHtmlBody(defaultBody);
      await fetchMails();
    } catch (err) {
      console.error(err);
      alert("Kaydetme hatası: konsola bakın.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (id, current) => {
    await updateDoc(doc(db, "scheduledMails", id), { active: !current });
    await fetchMails();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu mail görevini silmek istiyor musunuz?")) return;
    await deleteDoc(doc(db, "scheduledMails", id));
    if (selectedMail?.id === id) setSelectedMail(null);
    await fetchMails();
  };

  return (
    <div style={pageStyle}>
      <section style={panelStyle}>
        <h2 style={titleStyle}>Otomatik Mail Gönderici</h2>
        <div style={tagBoxStyle}>
          {Object.entries(TAG_HINTS).map(([tag, text]) => (
            <div key={tag} style={tagLineStyle}>
              <code>{tag}</code>
              <span>{text}</span>
            </div>
          ))}
        </div>
      </section>

      <form style={panelStyle} onSubmit={handleSave}>
        <h3 style={sectionTitleStyle}>Yeni Görev</h3>
        <div style={grid2Style}>
          <label style={fieldStyle}>
            <span>Konu</span>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} style={inputStyle} />
          </label>
          <label style={fieldStyle}>
            <span>Alıcılar</span>
            <input
              list="kontrol-mail-options"
              value={toInput}
              onChange={(e) => setToInput(e.target.value)}
              placeholder="ali@site.com, veli@site.com"
              style={inputStyle}
            />
            <datalist id="kontrol-mail-options">
              {emailOptions.map((email) => <option key={email} value={email} />)}
            </datalist>
          </label>
        </div>

        <div style={grid2Style}>
          <label style={fieldStyle}>
            <span>CC - Görünür kopya</span>
            <input
              list="kontrol-mail-options"
              value={ccInput}
              onChange={(e) => setCcInput(e.target.value)}
              placeholder="cc@site.com, cc2@site.com"
              style={inputStyle}
            />
          </label>
          <label style={fieldStyle}>
            <span>BCC - Gizli kopya</span>
            <input
              list="kontrol-mail-options"
              value={bccInput}
              onChange={(e) => setBccInput(e.target.value)}
              placeholder="gizli@site.com"
              style={inputStyle}
            />
          </label>
        </div>

        <div style={grid4Style}>
          <label style={fieldStyle}>
            <span>Periyot</span>
            <select value={periodType} onChange={(e) => setPeriodType(e.target.value)} style={inputStyle}>
              <option value="daily">Her gün</option>
              <option value="weekly-first-monday">Her haftanın ilk pazartesi</option>
              <option value="monthly-1">Her ayın 1'i</option>
              <option value="monthly-first-monday">Her ayın ilk pazartesi</option>
              <option value="special">Özel tarih</option>
            </select>
          </label>
          <label style={fieldStyle}>
            <span>Gönderim saati</span>
            <input type="time" value={sendHour} onChange={(e) => setSendHour(e.target.value)} style={inputStyle} />
          </label>
          <label style={fieldStyle}>
            <span>Süre (dk)</span>
            <input type="number" min={5} value={duration} onChange={(e) => setDuration(e.target.value)} style={inputStyle} />
          </label>
          {periodType === "special" && (
            <label style={fieldStyle}>
              <span>Özel tarih</span>
              <input type="date" value={specialDate} onChange={(e) => setSpecialDate(e.target.value)} style={inputStyle} />
            </label>
          )}
        </div>


        <label style={{ ...fieldStyle, display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="checkbox"
            checked={attachExcel}
            onChange={(e) => setAttachExcel(e.target.checked)}
            style={{ width: "auto" }}
          />
          Ayrıntılı uygunsuzluk listesini Excel eki olarak gönder
        </label>
        <label style={fieldStyle}>
          <span>HTML içerik</span>
          <textarea rows={12} value={htmlBody} onChange={(e) => setHtmlBody(e.target.value)} style={textareaStyle} />
        </label>

        <div style={previewStyle}>
          <div style={mutedStyle}>Önizleme</div>
          <div dangerouslySetInnerHTML={{ __html: highlightedPreview || "<em>Boş</em>" }} />
        </div>

        <button type="submit" disabled={saving} style={primaryButtonStyle}>
          {saving ? "Kaydediliyor..." : "Kaydet"}
        </button>
      </form>

      <section style={panelStyle}>
        <h3 style={sectionTitleStyle}>Kayıtlı Görevler</h3>
        <input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Konu veya alıcı ara"
          style={{ ...inputStyle, marginBottom: 12 }}
        />
        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Konu</th>
                <th style={thStyle}>Alıcı</th>
                <th style={thStyle}>Periyot</th>
                <th style={thStyle}>Saat</th>
                <th style={thStyle}>Sonraki çalışma</th>
                <th style={thStyle}>Durum</th>
                <th style={thStyle}>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {filteredList.map((item) => (
                <tr key={item.id}>
                  <td style={tdStyle}>{item.subject || "-"}</td>
                  <td style={tdStyle}>{Array.isArray(item.to) ? item.to.join(", ") : "-"}</td>
                  <td style={tdStyle}>{item.frequency || "-"}</td>
                  <td style={tdStyle}>{item.time || "-"}</td>
                  <td style={tdStyle}>{formatDate(item.nextRun)}</td>
                  <td style={tdStyle}>{item.active ? "Aktif" : "Pasif"}</td>
                  <td style={tdStyle}>
                    <div style={actionStyle}>
                      <button type="button" style={smallButtonStyle} onClick={() => setSelectedMail(item)}>Gör</button>
                      <button type="button" style={smallButtonStyle} onClick={() => handleToggleActive(item.id, item.active)}>
                        {item.active ? "Pasifleştir" : "Aktifleştir"}
                      </button>
                      <button type="button" style={dangerButtonStyle} onClick={() => handleDelete(item.id)}>Sil</button>
                    </div>
                  </td>
                </tr>
              ))}
              {!filteredList.length && (
                <tr>
                  <td colSpan={7} style={emptyStyle}>Kayıtlı kontrol formu mail görevi yok.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {selectedMail && (
        <section style={panelStyle}>
          <h3 style={sectionTitleStyle}>Seçili Şablon</h3>
          <pre style={codeBlockStyle}>{selectedMail.contentHtml || ""}</pre>
        </section>
      )}
    </div>
  );
}

const pageStyle = { display: "grid", gap: 14 };
const panelStyle = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 16 };
const titleStyle = { margin: "0 0 12px", fontSize: 22, color: "#111827" };
const sectionTitleStyle = { margin: "0 0 12px", fontSize: 17, color: "#111827" };
const tagBoxStyle = { display: "grid", gap: 8, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: 12 };
const tagLineStyle = { display: "grid", gridTemplateColumns: "220px 1fr", gap: 10, alignItems: "start", color: "#475569", fontSize: 13 };
const grid2Style = { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 };
const grid4Style = { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12, marginTop: 12 };
const fieldStyle = { display: "grid", gap: 6, marginBottom: 12, color: "#334155", fontSize: 13, fontWeight: 600 };
const inputStyle = { width: "100%", border: "1px solid #cbd5e1", borderRadius: 8, padding: "10px 11px", fontSize: 14, boxSizing: "border-box" };
const textareaStyle = { ...inputStyle, minHeight: 220, fontFamily: "Consolas, monospace", resize: "vertical" };
const previewStyle = { border: "1px dashed #94a3b8", borderRadius: 8, padding: 12, background: "#f8fafc", marginBottom: 12 };
const mutedStyle = { color: "#64748b", fontSize: 12, marginBottom: 8 };
const primaryButtonStyle = { border: 0, borderRadius: 8, background: "#0f172a", color: "#fff", padding: "10px 16px", fontWeight: 700, cursor: "pointer" };
const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const thStyle = { border: "1px solid #e2e8f0", background: "#f8fafc", padding: 9, textAlign: "left" };
const tdStyle = { border: "1px solid #e2e8f0", padding: 9, verticalAlign: "top" };
const emptyStyle = { ...tdStyle, textAlign: "center", color: "#64748b" };
const actionStyle = { display: "flex", gap: 6, flexWrap: "wrap" };
const smallButtonStyle = { border: "1px solid #cbd5e1", borderRadius: 7, background: "#fff", padding: "7px 9px", cursor: "pointer" };
const dangerButtonStyle = { ...smallButtonStyle, color: "#b91c1c", borderColor: "#fecaca" };
const codeBlockStyle = { background: "#0f172a", color: "#e2e8f0", borderRadius: 8, padding: 12, overflowX: "auto", whiteSpace: "pre-wrap" };
